import BraintreeService from "services/BraintreeService";
import DiscountService, { InvalidDiscountError } from "services/DiscountService";
import CountryService from "services/CountryService";
import jsFormFieldEventListener from '../utils/form_field'

const createState = (initialState) => {
  let state = initialState;

  return {
    get: () => state,
    set: (nextState) => {
      state = { ...state, ...nextState }

      document.dispatchEvent(
        new CustomEvent("stateChanged", { detail: state })
      );
    }
  }
}

const provideState = (state, component) =>
  Object.assign(component, {
    stateProvider: {
      getState: state.get,
      setState: state.set
    }
  });

const getUnitPriceTotalInCents = state =>
  Math.max(0, state.unitPriceInCents * state.quantity);

const getTotalPriceInCents = state =>
  Math.max(0, getUnitPriceTotalInCents(state) - state.discountInCents);

const updatePriceNodes = (container, cents, currency, prefix = "") => {
  const { code, symbol } = currency;
  const amount = (cents/100).toFixed(2).replace(/[.,]00$/, "");

  container
    .querySelector("[data-text=currency-symbol]")
    .textContent = `${prefix} ${symbol}`.trim();

  container.querySelector("[data-text=amount]").textContent = amount;
}

class OrderDetails {
  constructor(rootElement) {
    this.rootElement = rootElement

    this.quantitySelect = document.getElementById("quantity-select");
    this.unitTotalElement = document.querySelector("[data-element='unit-total-price']");
    this.totalPriceElement = document.querySelector("[data-element='total-price']");
    this.discountElement = document.getElementById("discount-item");
    this.discountAmountElement = document.querySelector("[data-element='discount-amount']");
    this.quantitySelect.addEventListener("change", this.onQuantitySelectChanged);

    document.addEventListener("stateChanged", e =>
      this.onStateChanged(e.detail))
  }

  onQuantitySelectChanged = e => {
    const quantity = e.target.value;
    const { discountCode } = this.stateProvider.getState();

    this.fetchDiscountAmountInCents(discountCode, quantity)
      .then(discountInCents => this.stateProvider.setState({ quantity, discountInCents }))
  }

  onStateChanged = state => {
    updatePriceNodes(this.unitTotalElement, getUnitPriceTotalInCents(state), state.currency)
    updatePriceNodes(this.totalPriceElement, getTotalPriceInCents(state), state.currency)
    updatePriceNodes(this.discountAmountElement, state.discountInCents, state.currency, "-")

    this.quantitySelect.value = state.quantity;
    this.discountElement.classList.toggle("hidden", state.discountInCents <= 0);
  }

  fetchDiscountAmountInCents = (code, quantity) =>
    new Promise(resolve => {
      if (!code) {
        return resolve(0)
      }

      DiscountService.discount(code, quantity).then(
        ({ discountInCents }) => resolve(discountInCents)
      );
    });
}

class DiscountForm {
  constructor(rootElement) {
    this.rootElement = rootElement
    this.rootElement.addEventListener("keyup", this.onKeyUp);
    this.rootElement.addEventListener("submit", this.onSubmit);
    this.discountAppliedMessage = rootElement.querySelector("[data-element=discount-success-message]")
    jsFormFieldEventListener(this.rootElement)
  }

  onKeyUp = e => {
    const { code, commit } = this.rootElement.elements;
    this.discountAppliedMessage.classList.add("hidden");
    this.setErrorMessage();
  }

  onSubmit = e => {
    e.preventDefault();

    const { quantity } = this.stateProvider.getState();
    const { code, commit } = this.rootElement.elements;
    const discountCode = code.value;

    if (!discountCode) {
      this.discountAppliedMessage.classList.add("hidden");
      this.setErrorMessage("This discount code is invalid.");
      return;
    }

    commit.disabled = true;

    this.setErrorMessage();

    DiscountService
      .discount(discountCode, quantity)
      .then(({ discountInCents }) => {
        this.discountAppliedMessage.classList.remove("hidden");
        this.stateProvider.setState({ discountCode, discountInCents })
      })
      .catch(error => {
        var message = "An error occurred."

        if (error instanceof InvalidDiscountError) {
          message = "This discount code is invalid."
        }

        this.setErrorMessage(message);
      })
      .finally(() => commit.disabled = false)
  }

  setErrorMessage = message => {
    const codeInput = this.rootElement.elements.code;
    const errorContainer =
      this.rootElement.querySelector("[data-element=validation-error-code]");
    const errorMessageContainer =
      errorContainer.querySelector("[data-text=error-content]");

    if (message) {
      errorMessageContainer.textContent = message;
      errorContainer.classList.remove("hidden");
      codeInput.classList.add("ga-form-input-invalid")
    } else {
      errorContainer.classList.add("hidden");
      codeInput.classList.remove("ga-form-input-invalid")
    }
  }
}

class PaymentForm {
  #form;

  #button;

  #braintreeService;

  /**
   *
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement} button
   * @param {BraintreeService} braintreeService
   */
  constructor(form, button, braintreeService) {
    this.#form = form;
    this.#button = button;
    this.#braintreeService = braintreeService;


    form.addEventListener("submit", this.onSubmit);
    form.addEventListener("change", this.onChange);
    form.addEventListener("blur", this.onBlur, true);
    form.addEventListener("invalid", this.onInvalid, true);
    jsFormFieldEventListener(this.#form)

    document.addEventListener("stateChanged", this.onStateChanged.bind(this))
  }

  onChange = ({ target }) => {
    target.dataset.touched = true;
    target.classList.remove('ga-form-input-invalid');
    target.parentNode.querySelector('.ga-form-input-error')?.classList?.add('hidden');

    if (target.name === "billing_address[country_code_alpha2]") {
      this.updateRegionSelectOptions(target.value)
      this.updateConditionalValidation(target.value);
    }

    if (target.name === "methods[payment_method_nonce]") {
      this.updateFieldVisibility()
    }
  }

  onBlur({ target }) {
    target.dataset.touched && target.checkValidity();
  }

  onInvalid({ target }) {
    target.classList.add('ga-form-input-invalid');
    target.parentNode.querySelector('.ga-form-input-error')?.classList?.remove('hidden');
  }

  onStateChanged({ detail: { discountCode, discountInCents, quantity } }) {
    this.updateFieldVisibility()
    this.#form.elements['discount_code'].value = discountCode
    this.#form.elements['discount_amount'].value = discountInCents
    this.#form.elements['quantity'].value = quantity
  }

  formValidity = selectedPaymentOption => {
    const braintreeState = this.#braintreeService.braintreeHostedFields.getState();
    const isBillingValid = !!selectedPaymentOption.billing_address_id || this.#form.checkValidity();
    const isCardValid = !!selectedPaymentOption.nonce || Object
      .keys(braintreeState.fields)
      .every(key => braintreeState.fields[key].isValid);

    return [isCardValid, isBillingValid];
  }

  updateValidationVisibility = (braintreeState, isCardValid, isBillingValid) => {
    const braintreeFieldNames = ["number", "expirationMonth", "expirationYear", "cvv"]
    const braintreeFields = braintreeState.fields;

    var getFirstInvalidBraintreeFieldName = () =>
      braintreeFieldNames.find(key => !braintreeFields[key].isValid);

    var getFirstInvalidBillingAddressField = () =>
      this.#form.querySelector(":not(fieldset):invalid");

    const firstInvalidFieldKey =
      (!isCardValid && getFirstInvalidBraintreeFieldName()) ||
      (!isBillingValid && getFirstInvalidBillingAddressField()?.name) ||
      null;

    if (firstInvalidFieldKey) {
      if (braintreeFieldNames.includes(firstInvalidFieldKey)) {
        document
          .getElementById(`waypoint-${firstInvalidFieldKey}`)
          .scrollIntoView({ behavior: "smooth" });
      } else {
        getFirstInvalidBillingAddressField()
          .parentElement
          .scrollIntoView({ behavior: "smooth" });
      }
    }

    Object.keys(braintreeFields).forEach(key => {
      const { container, isValid } = braintreeFields[key];
      const errorContainer =
        document.querySelector(`[data-element=validation-error-${key}]`);
        container.classList.toggle("ga-form-input-invalid", !isValid)
        errorContainer.classList.toggle("hidden", isValid)
    });
  }

  onSubmit = async (e) => {
    e.preventDefault();

    const state = this.stateProvider.getState();
    const selectedPaymentOption = this.getSelectedPaymentOption();
    const [isCardValid, isBillingValid] = this.formValidity(selectedPaymentOption)
    const isFormValid = isCardValid && isBillingValid;
    const braintreeState = this.#braintreeService.braintreeHostedFields.getState();

    this.#button.disabled = true;

    //console.log(getTotalPriceInCents(state));

    // If it's free, there's no credit card processing to do.
    if (getTotalPriceInCents(state) === 0) {
      this.#form.removeEventListener("submit", this.onSubmit);
      this.#form.submit();
      return true;
    }

    this.updateValidationVisibility(braintreeState, isCardValid, isBillingValid);

    if (!isFormValid) {
      this.#button.disabled = false;
      return false;
    }

    this.getFinalPaymentMethodNonce()
      .then(nonce => {
        this.#form.elements["billing_address_id"].value = selectedPaymentOption.billing_address_id || "";
        this.#form.elements["credit_card[payment_method_nonce]"].value = nonce;
        this.#form.removeEventListener("submit", this.onSubmit);
        this.#form.submit();
      })
      .catch(() => this.#button.disabled = false);
  }

  updateConditionalValidation(countryCode) {
    const fieldRequirements = {
      "billing_address[postal_code]": countryCode === "US",
      "billing_address[region]": countryCode === "US"
    }

    Object.entries(fieldRequirements).forEach(([name, required]) => {
      this.#form.elements[name].required = required;
      this.#form.elements[name].value = '';

      const spanElement = this.#form.elements[name].labels[0].querySelector('span')
      spanElement.classList.toggle("text-crimson", required)
      spanElement.classList.toggle("text-cloud", !required)

      this.#form.elements[name].dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  async updateRegionSelectOptions(countryCode) {
    const regionSelect = this.#form.elements["billing_address[region]"];
    const regions = await CountryService.getSubdivisions(countryCode);

    regionSelect.innerHTML = null;
    regionSelect.add(new Option("Select", ''));
    regions.forEach(item => regionSelect.add(new Option(item.name, item.code)));
    regionSelect.dispatchEvent(new Event("focus", { bubbles: true }))
  }

  updateFieldVisibility = () => {
    const { nonce, billing_address_id } = this.getSelectedPaymentOption();
    this.#form.elements["braintree_fields"].classList.toggle("hidden", nonce)
    this.#form.elements["billing_fields"].classList.toggle("hidden", billing_address_id)
  }

  getSelectedPaymentOption = () => {
    const state = this.stateProvider.getState();
    const nonce = this.#form.elements["methods[payment_method_nonce]"].value
    return state.paymentMethods.find(x => x.nonce === nonce);
  }

  getFinalPaymentMethodNonce = async () => {
    const [nonce, bin] = await this.getSelectedPaymentOptionDetails();

    return await this.getVerifiedPaymentMethodNonce(nonce, bin);
  }

  getSelectedPaymentOptionDetails = async () => {
    const { braintreeHostedFields } = this.#braintreeService;
    const { nonce, bin } = this.getSelectedPaymentOption()

    if (nonce && bin) { return [nonce, bin]; }

    const tokenized = await braintreeHostedFields.tokenize();

    return [tokenized.nonce, tokenized.details.bin];
  }

  getVerifiedPaymentMethodNonce = async (nonce, bin) => {
    const state = this.stateProvider.getState();
    const { braintreeThreeDSecure } = this.#braintreeService;

    if (!braintreeThreeDSecure) {
      return nonce;
    }

    const verification = await braintreeThreeDSecure.verifyCard({
      onLookupComplete: (_data, next) => next(),
      amount: (getTotalPriceInCents(state)/100),
      additionalInfo: {
        acsWindowSize: "03"
      },
      nonce,
      bin,
      challengeRequested: true,
    })

    return verification.nonce;
  }
}

window.initializePage = async (initialState, serviceConfigs) => {
  const state = createState(initialState);
  const braintreeConfig = {
    ...serviceConfigs.braintree,
    hostedFieldsEventHandlerFactory:
      hostedFieldsInstance => ({ emittedBy, fields }) => {
        const { container, isEmpty, isValid } = fields[emittedBy];
        const errorContainer =
          document.querySelector(`[data-element=validation-error-${emittedBy}]`);

        if (isEmpty || isValid) {
          container.classList.remove("ga-form-input-invalid");
          errorContainer.classList.add("hidden");
        } else {
          container.classList.add("ga-form-input-invalid");
          errorContainer.classList.remove("hidden");
        }

        const braintreeState = hostedFieldsInstance.getState();

        var braintreeFormIsValid = Object
          .keys(braintreeState.fields)
          .every(key => braintreeState.fields[key].isValid);

        state.set({ braintreeFormIsValid, braintreeFields: braintreeState.fields });
      }
  }

  const braintreeService = await new BraintreeService().configure(braintreeConfig)
  const paymentFormElement = document.forms["payment-form"];
  const paymentFormButton = document.querySelector("[form=payment-form]");

  braintreeService.braintreeDataCollector.getDeviceData().then(data => {
    paymentFormElement.elements["device_data"].value = data
  })

  provideState(state, new OrderDetails(document.getElementById("order-details")));
  provideState(state, new DiscountForm(document.getElementById("discount-form")));
  provideState(state, new PaymentForm(paymentFormElement, paymentFormButton, braintreeService));

  document.body.classList.add("js-page-initialized");
  document.getElementById("loading-message").classList.add("hidden");
  document.getElementById("cwe-payment-section").classList.remove("hidden");

  state.set({});
}
